import * as THREE from "three";
import {FBXLoader} from "three/examples/jsm/loaders/FBXLoader";
import {GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils";
import {AVATAR_ANIMATION} from "utils/constants";

class Model {
  #model;
  static #instanceCache = {};
  static #animationCache = {};

  constructor() {
    this.glTFLoader = new GLTFLoader();
    this.fbxLoader = new FBXLoader();
    this.textureLoader = new THREE.TextureLoader();
  }

  // NOTE:
  // this.#model would be look like
  // {
  //   object: model object3D
  //   animation: model animations
  // }
  async load(url, animationMap, animationUrl) {
    if (!Model.#instanceCache[url]) {
      const model = await this.#loadModel(url);
      // if (animationUrl) {
      //   let animationCache;
      //   if (Model.#animationCache[animationUrl]) {
      //     animationCache = Model.#animationCache[animationUrl];
      //   } else {
      //     animationCache = await this.#loadModel(animationUrl);
      //     Model.#animationCache[animationUrl] = animationCache;
      //   }
      //   model.animation = animationCache.animation;
      // }
      Model.#instanceCache[url] = model;
    }
    this.#model = this.#cloneModel(url);

    await this.#setAnimation(animationMap, url, animationUrl);
    this.#model.object.manager = this;
  }

  get model() {
    return this.#model.object;
  }
  get animation() {
    return this.#model.animation;
  }
  get animationAction() {
    return this.#model.animationAction;
  }

  unload(url, animationUrl) {
    if (!Model.#instanceCache[url]) {
      console.error(`${url} is not loaded`);
      return;
    }
    if (animationUrl) {
      delete Model.#animationCache[animationUrl];
    }
    delete Model.#instanceCache[url];
  }

  async #loadModel(url) {
    // TODO: there are more extensions. need to consider them.
    const isFBX = url.slice(-3).toLowerCase() === "fbx";
    const loader = isFBX ? this.fbxLoader : this.glTFLoader;

    return new Promise((resolve, reject) => {
      loader.load(
        url,
        object => {
          const model = isFBX ? object : object.scene;

          model.traverse(child => {
            if (child.type === "mesh" || child.isMesh) {
              child.material.metalness = 0.2;
              child.material.format = THREE.RGBAFormat;
              child.material.needsUpdate = true;
            }
          });

          resolve({
            object: model,
            animation: object.animations,
            animationAction: {},
          });
        },
        undefined,
        e => {
          console.error(e);
          reject(e);
        },
      );
    });
  }

  async #loadAnimation(animationUrl) {
    const isFBX = animationUrl.slice(-3).toLowerCase() === "fbx";
    const loader = isFBX ? this.fbxLoader : this.glTFLoader;

    return new Promise((resolve, reject) => {
      loader.load(
        animationUrl,
        object => {
          resolve({
            animation: object.animations,
          });
        },
        undefined,
        e => {
          console.error(e);
          reject(e);
        },
      );
    });
  }

  async #loadSingleGlb(url) {
    return new Promise((resolve, reject) => {
      this.glTFLoader.load(
        url,
        object => {
          resolve(object.animations[0]);
        },
        undefined,
        e => {
          console.error(e);
          reject(e);
        },
      );
    });
  }

  #cloneModel(url) {
    if (!Model.#instanceCache[url]) {
      throw new Error("There is no model created by this url");
    }
    return {
      object: SkeletonUtils.clone(Model.#instanceCache[url].object),
      animation: Model.#instanceCache[url].animation,
      animationAction: {},
    };
  }

  async #setAnimation(animationMap, url, animationUrl) {
    if (this.#model.animation === undefined || animationMap === undefined) return;
    const aniMap = animationMap;
    if (animationMap.model === "bear" || animationMap.model === "rpm")
      this.animationMixer = new THREE.AnimationMixer(this.#model.object.children[0]);
    else this.animationMixer = new THREE.AnimationMixer(this.#model.object);
    // this.animation = this.#model.animation;
    if (animationUrl) {
      const res = await this.#loadAnimation(animationUrl);
      Model.#instanceCache[url].animation = res.animation;
      this.#model.animation = res.animation;
    }

    //TODO addEventListener
    let nClip;
    if (aniMap.type === "label") {
      // set default animation
      this.#model.animationAction[AVATAR_ANIMATION.IDLE] = {
        action: this.animationMixer.clipAction(this.#model.animation[aniMap.map[AVATAR_ANIMATION.IDLE]]),
      };
      Object.values(aniMap.map[AVATAR_ANIMATION.JUMP]).forEach(val => {
        let idxs = [];
        this.#model.animation[val].tracks.forEach((track, i) => {
          if (track.name.endsWith("position")) {
            idxs.unshift(i);
          }
        });
        idxs.forEach(idx => this.#model.animation[val].tracks.splice(idx, 1));
      });

      Object.keys(AVATAR_ANIMATION).forEach(key => {
        if (aniMap.map[key]) {
          if (typeof aniMap.map[key] === "object") {
            const animationFlow = aniMap.map[key];
            this.#model.animationAction[key] = {};

            Object.keys(animationFlow).forEach(key2 => {
              this.#model.animationAction[key][key2] = this.animationMixer.clipAction(this.#model.animation[animationFlow[key2]]);
            });
          } else {
            this.#model.animationAction[key] = {
              action: this.animationMixer.clipAction(this.#model.animation[aniMap.map[key]]),
            };
          }
        } else {
          // If there is no aniMap info, idle animation will be set as default.
          this.#model.animationAction[key] = {
            action: this.#model.animationAction[AVATAR_ANIMATION.IDLE].action,
          };
        }
      });
      // Model.#instanceCache[url].animationAction = this.#model.animationAction; // 최초 한번 필요?
    } else if (aniMap.type === "frame") {
      nClip = THREE.AnimationUtils.subclip(
        this.#model.animation[0],
        AVATAR_ANIMATION.IDLE,
        aniMap.map[AVATAR_ANIMATION.IDLE].start,
        aniMap.map[AVATAR_ANIMATION.IDLE].end,
      );

      this.#model.animationAction[AVATAR_ANIMATION.IDLE] = {
        action: this.animationMixer.clipAction(nClip),
      };

      Object.keys(AVATAR_ANIMATION).forEach(key => {
        if (aniMap.map[key]) {
          nClip = THREE.AnimationUtils.subclip(this.#model.animation[0], key, aniMap.map[key].start, aniMap.map[key].end);

          this.#model.animationAction[key] = {
            action: this.animationMixer.clipAction(nClip),
          };
        } else {
          this.#model.animationAction[key] = {
            action: this.#model.animationAction[AVATAR_ANIMATION.IDLE].action,
          };
        }
      });
      // Model.#instanceCache[url].animationAction = this.#model.animationAction; // 최초 한번 필요?
    } else if (aniMap.type === "individual") {
      const idleClip = await this.#loadSingleGlb(aniMap.map[AVATAR_ANIMATION.IDLE]);
      idleClip.name = AVATAR_ANIMATION.IDLE;
      this.#model.animationAction[AVATAR_ANIMATION.IDLE] = {
        action: this.animationMixer.clipAction(idleClip),
      };
      const runClip = await this.#loadSingleGlb(aniMap.map[AVATAR_ANIMATION.RUN]);
      runClip.name = AVATAR_ANIMATION.RUN;
      this.#model.animationAction[AVATAR_ANIMATION.RUN] = {
        action: this.animationMixer.clipAction(runClip),
      };
      const jumpClip = await this.#loadSingleGlb(aniMap.map[AVATAR_ANIMATION.JUMP]);
      jumpClip.name = AVATAR_ANIMATION.JUMP;
      // Delete the position track to eliminate offset.
      jumpClip.tracks.splice(0, 1);
      this.#model.animationAction[AVATAR_ANIMATION.JUMP] = {
        action: this.animationMixer.clipAction(jumpClip),
      };

      const boostClip = await this.#loadSingleGlb(aniMap.map[AVATAR_ANIMATION.BOOST]);
      boostClip.name = AVATAR_ANIMATION.BOOST;
      this.#model.animationAction[AVATAR_ANIMATION.BOOST] = {
        action: this.animationMixer.clipAction(boostClip),
      };

      const fallClip = await this.#loadSingleGlb(aniMap.map[AVATAR_ANIMATION.FALL]);
      fallClip.name = AVATAR_ANIMATION.FALL;
      this.#model.animationAction[AVATAR_ANIMATION.FALL] = {
        action: this.animationMixer.clipAction(fallClip),
      };

      // [TEMP] RPM avatar doesn't have selfie animation
      this.#model.animationAction[AVATAR_ANIMATION.SELFIE] = this.#model.animationAction[AVATAR_ANIMATION.IDLE];
    }
  }
}

export default Model;
